// import node module libraries
import React, { useContext, Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { v4 as uuid } from 'uuid';
import {
  Accordion,
  Card,
  useAccordionButton,
  AccordionContext,
  OverlayTrigger,
  Tooltip,
  Modal,
  Button,
  Badge
} from "react-bootstrap";
import AlertModal from "components/elements/modal-alert/AlertModal";
import SurveyViewer from "components/elements/survey-viewer/SurveyViewer";
import { useMutation } from "@apollo/client/react";
import { CREATE_QUIZ_ANSWER } from "helper/graphql/mutations";
import { Notify } from "notiflix";
import AddQuizForm from "components/marketing/pages/courses/add-new-course/steps/quiz/AddQuizForm";

const QuiZAnswer = ({ quiz }) => {
  const [quizModal, setQUizModal] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  const [onCreateAnswer] = useMutation(CREATE_QUIZ_ANSWER);

  const handleComplete = (data) => {
    const payload = {
      "quizQuestionId": quiz?.guid,
      "userId": user?.guid,
      "isInstructorAnswer": true,
      "answerData": JSON.stringify(data),
    };

    onCreateAnswer({
      variables: payload,
      onCompleted: () => {
        setQUizModal(false);
        Notify.success('Quiz answers saved successfully!')
      },
      onError: () => Notify.failure("Failed to save quiz answers!"),
    });
  }

  return (
    <>
      <Button size="sm" className="action-btn answer-btn" onClick={() => setQUizModal(true)}>
        <i className="fe fe-clipboard me-1"></i>
        Add Answers
      </Button>

      <Modal
        show={quizModal}
        onHide={() => setQUizModal(false)}
        backdrop="static"
        keyboard={false}
        size="xl"
        className="modern-modal"
      >
        <Modal.Header closeButton className="modal-header-modern">
          <div className="modal-title-wrapper">
            <i className="fe fe-help-circle me-3 modal-icon"></i>
            <div>
              <h4 className="mb-0">Quiz Answers</h4>
              <p className="text-muted small mb-0">Provide correct answers for this quiz</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          <SurveyViewer
            questions={quiz?.questionData}
            onComplete={(data) => handleComplete(data)}
          />
        </Modal.Body>
      </Modal>

      <style jsx>{`
        :global(.action-btn) {
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.8125rem;
          padding: 0.5rem 1rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        :global(.answer-btn) {
          background: rgba(112, 165, 83, 0.1);
          border: 1px solid #70a553;
          color: #70a553;
        }

        :global(.answer-btn:hover) {
          background: #70a553;
          border-color: #70a553;
          color: white;
          transform: translateY(-2px);
        }

        :global(.modern-modal .modal-header-modern) {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        :global(.modern-modal .modal-header-modern .btn-close) {
          filter: brightness(0) invert(1);
        }

        .modal-title-wrapper {
          display: flex;
          align-items: center;
        }

        .modal-icon {
          font-size: 2rem;
          opacity: 0.9;
        }

        .modal-title-wrapper h4 {
          color: white;
          font-weight: 700;
        }

        .modal-title-wrapper .text-muted {
          color: rgba(255, 255, 255, 0.8) !important;
        }
      `}</style>
    </>
  );
}

const QuiZEdit = ({ quiz, module }) => {
  const [quizModal, setQUizModal] = useState(false);

  return (
    <>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Edit Quiz</Tooltip>}
      >
        <button className="icon-action-btn edit-btn" onClick={() => setQUizModal(true)}>
          <i className="fe fe-edit-2"></i>
        </button>
      </OverlayTrigger>

      <Modal
        show={quizModal}
        onHide={() => setQUizModal(false)}
        backdrop="static"
        keyboard={false}
        size="xl"
        className="modern-modal"
      >
        <AddQuizForm
          quiz={quiz}
          onSuccess={() => {
            setQUizModal(false)
          }}
          module={module}
        />
      </Modal>

      <style jsx>{`
        .icon-action-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: none;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        .edit-btn {
          color: #1d9eb0;
        }

        .edit-btn:hover {
          background: rgba(29, 158, 176, 0.1);
          color: #1d9eb0;
          transform: scale(1.1);
        }
      `}</style>
    </>
  );
}

const GKAccordionActions = ({ accordionItems, handleDelete, isQuiz = false, module }) => {
  const ContextAwareToggle = ({ children, eventKey, callback }) => {
    const currentEventKey = useContext(AccordionContext);
    const decoratedOnClick = useAccordionButton(
      eventKey,
      () => callback && callback(eventKey)
    );
    const isCurrentEventKey = currentEventKey === eventKey;
    const overlayKeyDelete = uuid();

    const [deleteModal, setDeleteModal] = useState(false);

    return (
      <Fragment>
        <div className="accordion-header-modern">
          <div className="accordion-title-section" onClick={decoratedOnClick}>
            <div className="expand-icon">
              <i className={`fe fe-chevron-${isCurrentEventKey ? 'down' : 'right'}`}></i>
            </div>
            <h5 className="accordion-title mb-0">{children}</h5>
          </div>
          
          <div className="accordion-actions">
            {isQuiz && (
              <>
                <QuiZAnswer quiz={accordionItems.find(item => item.guid === eventKey)} />
                <QuiZEdit quiz={accordionItems.find(item => item.guid === eventKey)} module={module} />
              </>
            )}

            <OverlayTrigger
              key={overlayKeyDelete}
              placement="top"
              overlay={<Tooltip>Delete</Tooltip>}
            >
              <button className="icon-action-btn delete-btn" onClick={() => setDeleteModal(true)}>
                <i className="fe fe-trash-2"></i>
              </button>
            </OverlayTrigger>
          </div>
        </div>

        <AlertModal
          isOpen={deleteModal}
          title={'Delete Item'}
          message={"Are you sure you want to delete this item? This action cannot be undone."}
          handleYes={() => handleDelete(eventKey)}
          onClose={() => setDeleteModal(false)}
        />

        <style jsx>{`
          .accordion-header-modern {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 0.75rem 0;
          }

          .accordion-title-section {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex: 1;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .accordion-title-section:hover .accordion-title {
            color: #F68521;
          }

          .expand-icon {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            flex-shrink: 0;
          }

          .accordion-title-section:hover .expand-icon {
            background: rgba(246, 133, 33, 0.1);
          }

          .expand-icon i {
            color: #64748b;
            transition: all 0.3s ease;
          }

          .accordion-title-section:hover .expand-icon i {
            color: #F68521;
          }

          .accordion-title {
            font-size: 1rem;
            font-weight: 600;
            color: #1e293b;
            transition: color 0.3s ease;
          }

          .accordion-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-shrink: 0;
          }

          .icon-action-btn {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            border: none;
            background: transparent;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 1rem;
          }

          .delete-btn {
            color: #dc2626;
          }

          .delete-btn:hover {
            background: rgba(220, 38, 38, 0.1);
            color: #dc2626;
            transform: scale(1.1);
          }
        `}</style>
      </Fragment>
    );
  };

  // Empty state
  if (!accordionItems || accordionItems.length === 0) {
    return (
      <div className="empty-accordion-state">
        <div className="empty-icon">
          <i className="fe fe-inbox"></i>
        </div>
        <p className="empty-text">No items added yet</p>
        <p className="empty-subtext">Click the button above to add your first item</p>

        <style jsx>{`
          .empty-accordion-state {
            text-align: center;
            padding: 3rem 2rem;
            background: #f8f9fa;
            border-radius: 12px;
            border: 2px dashed #e2e8f0;
          }

          .empty-icon {
            font-size: 3rem;
            color: #cbd5e1;
            margin-bottom: 1rem;
          }

          .empty-text {
            font-size: 1rem;
            font-weight: 600;
            color: #64748b;
            margin-bottom: 0.5rem;
          }

          .empty-subtext {
            font-size: 0.875rem;
            color: #94a3b8;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  return (
    <Fragment>
      <Accordion defaultActiveKey={accordionItems[0]?.guid} className="accordion-modern">
        {accordionItems?.map((item, index) => (
          <Card key={item?.guid} className="accordion-card-modern mb-2">
            <Card.Header className="accordion-card-header">
              <ContextAwareToggle eventKey={item?.guid}>
                {item?.title || item?.name || item?.contentType}
              </ContextAwareToggle>
            </Card.Header>
            <Accordion.Collapse eventKey={item?.guid}>
              <Card.Body className="accordion-card-body">
                {item?.description ? (
                  <p className="description-text">{item?.description}</p>
                ) : (
                  <p className="no-description">No description provided</p>
                )}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        ))}
      </Accordion>

      <style jsx>{`
        :global(.accordion-modern) {
          --bs-accordion-border-width: 0;
        }

        :global(.accordion-card-modern) {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        :global(.accordion-card-modern:hover) {
          border-color: #cbd5e1;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }

        :global(.accordion-card-header) {
          background: white;
          border: none;
          padding: 1rem 1.25rem;
        }

        :global(.accordion-card-body) {
          padding: 1.25rem;
          background: #f8f9fa;
          border-top: 1px solid #e2e8f0;
        }

        .description-text {
          color: #64748b;
          line-height: 1.6;
          margin: 0;
          font-size: 0.9375rem;
        }

        .no-description {
          color: #94a3b8;
          font-style: italic;
          margin: 0;
          font-size: 0.875rem;
        }
      `}</style>
    </Fragment>
  );
};

export default GKAccordionActions;